/**
 * Inline formatting dialect stored inside paragraph / title / quote strings.
 *
 *   **bold**  *italic*  ++underline++  ~~strike~~  `code`  [label](url)
 *
 * Plain text (no markers) stays unchanged for backward compatibility.
 */

import type { JSONContent } from '@tiptap/react';
import type { ReactNode } from 'react';
import { createElement, Fragment } from 'react';

export type InlineNode =
    | { type: 'text'; text: string }
    | { type: 'break' }
    | {
          type: 'bold' | 'italic' | 'underline' | 'strike' | 'code';
          children: InlineNode[];
      }
    | { type: 'link'; href: string; children: InlineNode[] };

const ESC_RE = /([\\`*~[\]_+])/g;

function escapeMarkdownLiteral(text: string): string {
    return text.replace(ESC_RE, '\\$1');
}

function unescapeMarkdownLiteral(text: string): string {
    return text.replace(/\\([\\`*~[\]_+])/g, '$1');
}

/** TipTap inline JSON → markdown string. */
export function tipTapInlineToMarkdown(
    nodes: JSONContent[] | undefined,
): string {
    if (!nodes?.length) return '';
    let out = '';
    for (const node of nodes) {
        if (node.type === 'hardBreak') {
            out += '\n';
            continue;
        }
        if (node.type !== 'text') {
            if (node.content) out += tipTapInlineToMarkdown(node.content);
            continue;
        }
        out += applyMarksToText(node.text ?? '', node.marks ?? []);
    }
    return out;
}

function applyMarksToText(
    rawText: string,
    marks: NonNullable<JSONContent['marks']>,
): string {
    if (!rawText && marks.length === 0) return '';

    const hasCode = marks.some((m) => m.type === 'code');
    let text = hasCode
        ? `\`${rawText.replace(/`/g, '\\`')}\``
        : escapeMarkdownLiteral(rawText);

    const order = ['strike', 'underline', 'italic', 'bold', 'link'] as const;
    for (const type of order) {
        if (hasCode && type !== 'link') continue;
        const mark = marks.find((m) => m.type === type);
        if (!mark) continue;
        switch (type) {
            case 'bold':
                text = `**${text}**`;
                break;
            case 'italic':
                text = `*${text}*`;
                break;
            case 'underline':
                text = `++${text}++`;
                break;
            case 'strike':
                text = `~~${text}~~`;
                break;
            case 'link':
                text = `[${text}](${String(mark.attrs?.href ?? '').trim()})`;
                break;
        }
    }
    return text;
}

/** Markdown string → TipTap inline JSON. */
export function markdownToTipTapInline(markdown: string): JSONContent[] {
    const tree = parseInlineMarkdown(markdown);
    return treeToTipTap(tree);
}

/** Markdown string → React nodes for public rendering. */
export function renderInlineMarkdown(markdown: string): ReactNode {
    const tree = parseInlineMarkdown(markdown);
    return treeToReact(tree);
}

/** Strip markers for SEO / embeddings / cards. */
export function stripInlineMarkdown(markdown: string): string {
    return flattenTree(parseInlineMarkdown(markdown));
}

function flattenTree(nodes: InlineNode[]): string {
    let out = '';
    for (const node of nodes) {
        if (node.type === 'text') out += node.text;
        else if (node.type === 'break') out += '\n';
        else if ('children' in node) out += flattenTree(node.children);
    }
    return out;
}

function treeToTipTap(nodes: InlineNode[], marks: JSONContent['marks'] = []): JSONContent[] {
    const out: JSONContent[] = [];
    for (const node of nodes) {
        if (node.type === 'break') {
            out.push({ type: 'hardBreak' });
            continue;
        }
        if (node.type === 'text') {
            if (!node.text) continue;
            out.push({
                type: 'text',
                text: node.text,
                ...(marks.length ? { marks: [...marks] } : {}),
            });
            continue;
        }
        if (node.type === 'link') {
            out.push(
                ...treeToTipTap(node.children, [
                    ...marks,
                    { type: 'link', attrs: { href: node.href } },
                ]),
            );
            continue;
        }
        out.push(
            ...treeToTipTap(node.children, [...marks, { type: node.type }]),
        );
    }
    return mergeAdjacentText(out);
}

function mergeAdjacentText(nodes: JSONContent[]): JSONContent[] {
    const out: JSONContent[] = [];
    for (const node of nodes) {
        const prev = out[out.length - 1];
        if (
            prev?.type === 'text' &&
            node.type === 'text' &&
            JSON.stringify(prev.marks ?? null) ===
                JSON.stringify(node.marks ?? null)
        ) {
            prev.text = `${prev.text ?? ''}${node.text ?? ''}`;
            continue;
        }
        out.push(node);
    }
    return out;
}

function treeToReact(nodes: InlineNode[], keyPrefix = 'i'): ReactNode {
    return nodes.map((node, i) => {
        const key = `${keyPrefix}-${i}`;
        if (node.type === 'break') return createElement('br', { key });
        if (node.type === 'text') return node.text;
        if (node.type === 'bold') {
            return createElement('strong', { key }, treeToReact(node.children, key));
        }
        if (node.type === 'italic') {
            return createElement('em', { key }, treeToReact(node.children, key));
        }
        if (node.type === 'underline') {
            return createElement('u', { key }, treeToReact(node.children, key));
        }
        if (node.type === 'strike') {
            return createElement('s', { key }, treeToReact(node.children, key));
        }
        if (node.type === 'code') {
            return createElement('code', { key }, treeToReact(node.children, key));
        }
        if (node.type === 'link') {
            return createElement(
                'a',
                {
                    key,
                    href: node.href,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                },
                treeToReact(node.children, key),
            );
        }
        return null;
    });
}

/**
 * Recursive markdown inline parser for our dialect.
 * Delimiters: ** * ++ ~~ ` [text](url) and \n as soft break.
 */
export function parseInlineMarkdown(input: string): InlineNode[] {
    const src = input.replace(/\r\n/g, '\n');
    let i = 0;

    function parseUntil(stop: ((rest: string) => number) | null): InlineNode[] {
        const nodes: InlineNode[] = [];
        let text = '';

        const flush = () => {
            if (!text) return;
            nodes.push({ type: 'text', text: unescapeMarkdownLiteral(text) });
            text = '';
        };

        while (i < src.length) {
            if (stop) {
                const n = stop(src.slice(i));
                if (n > 0) {
                    flush();
                    i += n;
                    return nodes;
                }
            }

            if (src[i] === '\\' && i + 1 < src.length) {
                text += src[i + 1];
                i += 2;
                continue;
            }

            if (src[i] === '\n') {
                flush();
                nodes.push({ type: 'break' });
                i += 1;
                continue;
            }

            if (src.startsWith('**', i)) {
                flush();
                i += 2;
                const children = parseUntil((rest) =>
                    rest.startsWith('**') ? 2 : 0,
                );
                nodes.push({ type: 'bold', children });
                continue;
            }

            if (src.startsWith('++', i)) {
                flush();
                i += 2;
                const children = parseUntil((rest) =>
                    rest.startsWith('++') ? 2 : 0,
                );
                nodes.push({ type: 'underline', children });
                continue;
            }

            if (src.startsWith('~~', i)) {
                flush();
                i += 2;
                const children = parseUntil((rest) =>
                    rest.startsWith('~~') ? 2 : 0,
                );
                nodes.push({ type: 'strike', children });
                continue;
            }

            if (src[i] === '*') {
                flush();
                i += 1;
                const children = parseUntil((rest) =>
                    rest.startsWith('*') && !rest.startsWith('**') ? 1 : 0,
                );
                nodes.push({ type: 'italic', children });
                continue;
            }

            if (src[i] === '`') {
                flush();
                i += 1;
                let code = '';
                while (i < src.length && src[i] !== '`') {
                    if (src[i] === '\\' && i + 1 < src.length) {
                        code += src[i + 1];
                        i += 2;
                        continue;
                    }
                    code += src[i];
                    i += 1;
                }
                if (src[i] === '`') i += 1;
                nodes.push({
                    type: 'code',
                    children: [{ type: 'text', text: code }],
                });
                continue;
            }

            if (src[i] === '[') {
                const closeBracket = src.indexOf(']', i + 1);
                if (
                    closeBracket !== -1 &&
                    src[closeBracket + 1] === '(' &&
                    src.indexOf(')', closeBracket + 2) !== -1
                ) {
                    const closeParen = src.indexOf(')', closeBracket + 2);
                    flush();
                    const label = src.slice(i + 1, closeBracket);
                    const href = src.slice(closeBracket + 2, closeParen).trim();
                    i = closeParen + 1;
                    nodes.push({
                        type: 'link',
                        href,
                        children: parseInlineMarkdown(label),
                    });
                    continue;
                }
            }

            text += src[i];
            i += 1;
        }

        flush();
        return nodes;
    }

    return parseUntil(null);
}

/** Wrap React output; empty → null. */
export function InlineMarkdown({
    text,
    as: Comp = Fragment,
}: {
    text: string;
    as?: 'span' | typeof Fragment;
}): ReactNode {
    if (!text) return null;
    const content = renderInlineMarkdown(text);
    if (Comp === Fragment) return createElement(Fragment, null, content);
    return createElement(Comp, null, content);
}
